<template>
  <main class="container">
    <h1 class="page-title" data-testid="page-title">Tell Us About You</h1>

    <BackButton class="back-link" />

    <form
      class="form personal-form"
      novalidate
      @submit.prevent="formSubmit"
      autocomplete="on"
    >
      <fieldset class="form__fieldset">
        <div class="form-item" :class="{ '--has-error': $v.leadType.$error }">
          <label class="form__label">
            <span class="form__label-text">Which Describes You?</span>
            <FormError
              v-if="$v.leadType.$error"
              msg="Please select an option"
              data-testid="lead-type-error"
            />
          </label>
          <div class="form__radiobuttons">
            <label
              for="studentLead"
              data-testid="student-lead"
              :class="{ active: leadType === 'Student' }"
            >
              <input
                type="radio"
                id="studentLead"
                name="leadType"
                v-model="leadType"
                value="Student"
                required
              />
              Student
            </label>
            <label
              for="notStudentLead"
              data-testid="notStudentLead"
              :class="{ active: leadType === 'Not a Student' }"
            >
              <input
                type="radio"
                id="notStudentLead"
                name="leadType"
                v-model="leadType"
                value="Not a Student"
                required
              />
              Not A Student
            </label>
          </div>
        </div>
        <div class="form-item" :class="{ '--has-error': $v.firstname.$error }">
          <label class="form__label" for="fname">
            <span class="form__label-text">First name</span>
            <FormError
              v-if="
                ($v.firstname.$error && !$v.firstname.required) ||
                  validationErrors.firstName
              "
              data-testid="first-name-error"
              :msg="
                validationErrors.firstName
                  ? validationErrors.firstName[0]
                  : 'First name required'
              "
            />
          </label>
          <input
            class="form__input"
            type="text"
            id="fname"
            data-testid="first-name"
            name="fname"
            autocomplete="given-name"
            v-model.trim="firstname"
            @blur="capitaliseName"
          />
        </div>

        <div class="form-item" :class="{ '--has-error': $v.lastname.$error }">
          <label class="form__label" for="lname">
            <span class="form__label-text">Last name</span>
            <FormError
              v-if="
                ($v.lastname.$error && !$v.lastname.required) ||
                  validationErrors.lastName
              "
              data-testid="last-name-required-error"
              :msg="
                validationErrors.lastName
                  ? validationErrors.lastName[0]
                  : 'Last name required'
              "
            />
          </label>
          <input
            class="form__input"
            type="text"
            id="lname"
            data-testid="last-name"
            name="lname"
            required
            autocomplete="family-name"
            v-model.trim="lastname"
            @blur="capitaliseName"
          />
        </div>

        <div class="form-item" :class="{ '--has-error': $v.email.$error }">
          <label class="form__label" for="email">
            <span class="form__label-text">Email address</span>
            <FormError
              v-if="$v.email.$error && !$v.email.required"
              data-testid="email-required-error"
              msg="Email address required"
            />
            <FormError
              v-if="
                ($v.email.$error && !$v.email.email) || validationErrors.email
              "
              data-testid="valid-email-required-error"
              :msg="
                validationErrors.email
                  ? validationErrors.email[0]
                  : 'Valid email address required'
              "
            />
          </label>
          <input
            class="form__input"
            type="text"
            id="email"
            data-testid="email"
            name="email"
            required
            autocomplete="email"
            v-model.trim="email"
          />
        </div>

        <div class="form-item">
          <label class="form__label" for="tel">
            <span class="form__label-text">Mobile number</span>
            <FormError
              v-if="
                ($v.tel.$error && !$v.tel.required) ||
                  validationErrors.phoneNumber
              "
              data-testid="phone-number-required-error"
              :msg="
                validationErrors.phoneNumber
                  ? validationErrors.phoneNumber[0]
                  : 'Mobile number required'
              "
            />
          </label>
          <input
            class="form__input"
            type="tel"
            id="tel"
            data-testid="mobile-number"
            name="tel"
            required
            autocomplete="tel"
            v-model.trim="tel"
          />
        </div>
      </fieldset>

      <WarningPanel
        v-if="errorResponse"
        title="Oops, you’re already joined onto this account"
        msg="Invite your remaining housemates to join, then sit back and relax. Your utilities will be in place for your start date."
        :severe="true"
      />

      <button
        class="btn btn--full-width btn--big form__submit"
        type="submit"
        @click="trackSubmitBtn"
        data-testid="next-button"
      >
        Next
      </button>

      <InfoBox class="full-width">
        <template v-slot:image>
          <CustomerIcon class="fill-brand-blue" />
        </template>
        <template v-slot:title>
          Every Housemate Is Equal
        </template>
        <template v-slot>
          Your housemates will add their details later and we won’t set up a
          thing until everyone has joined.
        </template>
      </InfoBox>
    </form>
  </main>
</template>

<script>
import { email, required } from "vuelidate/lib/validators";
import axiosInstance from "@/utility/axiosInstance";
import FormError from "@/components/forms/FormError";
import WarningPanel from "@/components/WarningPanel";
import InfoBox from "@/components/InfoBox";
import CustomerIcon from "@/assets/svg/icon-customer.svg";
import { mapActions, mapMutations } from "vuex";

export default {
  name: "PersonalDetails",
  data() {
    return {
      errorResponse: false,
    };
  },
  computed: {
    leadType: {
      get() {
        return this.$store.getters.leadType;
      },
      set(value) {
        this.$store.commit("SET_PERSONAL_DETAILS_PROP", { leadType: value });
      },
    },
    firstname: {
      get() {
        return this.$store.getters.firstName;
      },
      set(value) {
        this.$store.commit("SET_PERSONAL_DETAILS_PROP", { firstname: value });
      },
    },
    lastname: {
      get() {
        return this.$store.getters.lastName;
      },
      set(value) {
        this.$store.commit("SET_PERSONAL_DETAILS_PROP", { lastname: value });
      },
    },
    email: {
      get() {
        return this.$store.getters.emailAddress;
      },
      set(value) {
        this.$store.commit("SET_PERSONAL_DETAILS_PROP", { email: value });
      },
    },
    tel: {
      get() {
        return this.$store.getters.telNumber;
      },
      set(value) {
        this.$store.commit("SET_PERSONAL_DETAILS_PROP", { tel: value });
      },
    },
    validationErrors() {
      return this.$store.state.validationErrors;
    },
  },
  methods: {
    ...mapMutations({
      showLoadingPanel: "SHOW_LOADING_PANEL",
      hideLoadingPanel: "HIDE_LOADING_PANEL",
      capitaliseName: "CAPITALISE_NAME",
    }),
    ...mapActions(["setValidationErrors", "clearValidationErrors"]),
    formSubmit() {
      this.$v.$touch();

      if (!this.$v.$invalid) {
        // check email address for account duplication
        this.showLoadingPanel();
        this.errorResponse = false;

        this.capitaliseName();

        this.clearValidationErrors();

        axiosInstance
          .post("/validate-joining-tenant", {
            guid: this.$store.getters.guid,
            email: this.email,
            leadType: this.leadType,
            firstName: this.firstname,
            lastName: this.lastname,
            phoneNumber: this.tel,
          })
          .then(() => {
            // if this request succeeds, the email has already been
            // used for this guid
            this.errorResponse = true;
          })
          .catch((error) => {
            if (error.response && error.response.status === 422) {
              this.setValidationErrors(error.response.data.errors);
            } else {
              // if error, the API is either down or the email hasn't been used,
              // either way, proceed to the next step
              this.$router.push("/joining-tenant/direct-debit");
            }
          })
          .then(() => {
            this.hideLoadingPanel();
          });
      }
    },
    trackSubmitBtn() {
      this.trackGoogleEvent(
        "Click Next",
        "Joining Housemate",
        "Confirm personal details",
      );
    },
  },
  validations() {
    return {
      leadType: {
        required,
      },
      firstname: {
        required,
      },
      lastname: {
        required,
      },
      email: {
        required,
        email,
      },
      tel: {
        required,
      },
    };
  },
  created() {
    // ensure back btn is hidden
    this.$store.commit("SHOW_BACK_BTN");
  },
  components: {
    FormError,
    WarningPanel,
    CustomerIcon,
    InfoBox,
  },
};
</script>

<style lang="scss" scoped>
main {
  max-width: 60rem;
}

.btn--full-width {
  margin-bottom: $spacing-medium;
}
</style>
